function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import { ChevronDown } from "@bolteu/kalep-react-icons";
import { ClearInputButton } from "../_internal/ClearInputButton";
import { SpinnerIcon } from "../SpinnerIcon";
import { BaseTextField } from "../TextField/BaseTextField";
import { cx } from "../../helpers/utils/cx";
import { noopNull } from "../../helpers/utils/noop";
import { DEFAULT_LOADING_SPINNER_ARIA_LABEL, DEFAULT_TOGGLE_BUTTON_ARIA_LABEL } from "./BaseComboBox";
const sizes = {
  sm: "pr-2 pl-3",
  md: "pr-2 gap-2",
  lg: "pr-2 gap-2 pl-3"
};
export function ComboBoxTriggerSingle(props) {
  const {
    comboBoxId,
    clearable,
    controlledInputValue,
    disabled,
    error,
    fullWidth,
    id,
    isOpen,
    generatedId,
    getInputProps,
    getToggleButtonProps,
    inputValue,
    inputWrapperRef,
    loading,
    loadingSpinnerAriaLabel = DEFAULT_LOADING_SPINNER_ARIA_LABEL,
    // multiline, // TODO: Add support
    name,
    noResultsMessageId,
    onClear,
    onInputChange,
    openMenu,
    placeholder,
    required,
    shouldShowNoResultsMessage,
    shouldRenderEndSlotIcon = true,
    size = "md",
    toggleButtonAriaLabel = DEFAULT_TOGGLE_BUTTON_ARIA_LABEL,
    toggleButtonRef,
    value
  } = props;
  const inputRef = React.useRef(null);
  const inputA11yProps = getInputProps({
    ref: inputRef,
    disabled,
    onChange: onInputChange
  });
  const loadingSpinnerId = id ?? `kalep-combo-box-loading-spinner-${generatedId}`;
  if (loading) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${loadingSpinnerId}`;
  }
  if (shouldShowNoResultsMessage) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${noResultsMessageId}`;
  }
  const handleClearButtonClick = React.useCallback(() => {
    if (typeof onInputChange === "function" && controlledInputValue) {
      /* In case of controlled input call onInputChange */
      const event = {
        target: {
          value: ""
        }
      };
      onInputChange(event);
    } else {
      /* In case of uncontrolled input set input value to empty string */
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
      const input = inputWrapperRef?.current?.getElementsByTagName("input")?.[0];
      nativeInputValueSetter?.call(input, "");
      input?.dispatchEvent(new Event("input", {
        bubbles: true
      }));
      input?.focus();
    }
    onClear?.();
  }, [controlledInputValue, inputWrapperRef, onInputChange, onClear]);
  const renderEndSlot = React.useCallback(renderEndSlotProps => {
    if (props.renderEndSlot) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx("shrink-0 flex items-center max-w-6 gap-1")
      }, props.renderEndSlot(props, noopNull));
    }
    const showClearButton = !disabled && (!!value || !!inputValue) && clearable;
    return /*#__PURE__*/React.createElement("div", {
      className: cx("flex items-center gap-1")
    }, showClearButton && /*#__PURE__*/React.createElement(ClearInputButton, {
      onClick: handleClearButtonClick,
      size: size === "sm" ? "md" : "lg",
      buttonClassname: cx("hidden group-hover/trigger:block", isOpen && "block")
    }), loading && /*#__PURE__*/React.createElement(SpinnerIcon, {
      id: loadingSpinnerId,
      "aria-label": loadingSpinnerAriaLabel,
      className: cx("flex-none animate-spin text-action-secondary m-1"),
      size: size,
      role: "status"
    }), !loading && shouldRenderEndSlotIcon && /*#__PURE__*/React.createElement("button", _extends({
      type: "button"
    }, getToggleButtonProps({
      ref: toggleButtonRef,
      "aria-label": toggleButtonAriaLabel
    }), {
      className: cx("border-0 bg-transparent cursor-pointer m-0 p-0 flex items-center", renderEndSlotProps.disabled && "cursor-not-allowed"),
      disabled: renderEndSlotProps.disabled
    }), /*#__PURE__*/React.createElement(ChevronDown, {
      className: cx("flex-none transition-transform m-1", renderEndSlotProps.disabled ? "text-tertiary" : "text-primary", isOpen && "rotate-180"),
      size: size === "sm" ? "md" : "lg"
    })));
  }, [props, disabled, value, inputValue, clearable, handleClearButtonClick, size, isOpen, loading, loadingSpinnerId, loadingSpinnerAriaLabel, shouldRenderEndSlotIcon, getToggleButtonProps, toggleButtonRef, toggleButtonAriaLabel]);
  const renderInput = React.useCallback((inputRenderProps, defaultInputRender) => {
    return /*#__PURE__*/React.cloneElement(defaultInputRender(inputRenderProps), _objectSpread(_objectSpread({}, inputA11yProps), {}, {
      required: false,
      "aria-required": required
    }));
  }, [inputA11yProps, required]);
  const renderStartSlotForTextField = React.useCallback((textFieldRenderProps, defaultTextFieldRender) => {
    if (props.renderStartSlot) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx("shrink-0 flex items-center max-w-6")
      }, props.renderStartSlot(props, () => null));
    }
    return defaultTextFieldRender(textFieldRenderProps);
  }, [props]);
  const onClick = React.useCallback(e => {
    if (!toggleButtonRef?.current?.contains(e.target)) {
      openMenu();
    }
  }, [openMenu, toggleButtonRef]);
  return /*#__PURE__*/React.createElement(BaseTextField, {
    ref: inputRef,
    inputWrapperRef: inputWrapperRef,
    disabled: disabled,
    error: error,
    fullWidth: fullWidth,
    id: comboBoxId,
    name: name,
    onClick: onClick,
    overrideClassName: cx(sizes[size], "group/trigger"),
    placeholder: placeholder,
    renderEndSlot: renderEndSlot,
    renderInput: renderInput,
    size: size,
    value: inputValue,
    renderStartSlot: renderStartSlotForTextField
  });
}